import React from 'react';
import { RiPlaneLine, RiBuildingLine, RiMedalLine, RiAwardLine, RiCheckLine, RiPriceTag3Line, RiTrophyLine, RiLoader4Line, RiSendPlane2Line, RiDraftLine, RiCalendar2Line, RiTeamLine, RiMapPinLine, RiCloseLine } from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from '../../../../components/ui/Drawer';
import { TrustScore } from '../../../trust-score';
import { useAppStore } from '@store/appStore';
import { TrustJetPlane } from '@assets/images';
import ImageCarousel from '@components/ui/ImageCarousel';
import { formatPrice, formatDate } from '@/utils';
import { getAircraftModelName, getOperatorWithLocation, getAircraftPhotos } from '@/features/tails/utils';

// Format separate date + time strings for local display
const formatDateWithTime = (date, time) => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  if (time) {
    const [hours, minutes] = time.split(':');
    dateObj.setHours(parseInt(hours, 10), parseInt(minutes, 10));
  }
  const dateFormatted = dateObj.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
  const timeFormatted = dateObj.toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false
  });
  return `${dateFormatted} at ${timeFormatted}`;
};

// Tax rate constants (matching AircraftCard)
const BROKER_MARGIN_RATE = 0.15;
const FET_RATE = 0.075;
const PLATFORM_FEE_RATE = 0.015;
const CREDIT_CARD_FEE_RATE = 0.04;
const SEGMENT_FEE_PER_PAX = 5.20;

// Round to 2 decimal places (half-up)
const round2 = (num) => Math.round(num * 100) / 100;

// Calculate final price if not already set (matching AircraftCard Total Cost calculation)
const calculateFinalPrice = (option, passengerCount = 1, legMultiplier = 1) => {
  if (option.final_price && parseFloat(option.final_price) > 0) {
    return parseFloat(option.final_price);
  }
  // Calculate from operator cost
  const operatorCost = parseFloat(option.total_price) || 0;
  const brokerMarkup = round2(operatorCost * BROKER_MARGIN_RATE);
  const subtotal = operatorCost + brokerMarkup;
  const platformFee = round2(subtotal * PLATFORM_FEE_RATE);
  const federalExciseTax = round2(subtotal * FET_RATE);
  const segmentTax = round2(passengerCount * SEGMENT_FEE_PER_PAX * legMultiplier);
  const totalBeforeCCFee = subtotal + platformFee + federalExciseTax + segmentTax;
  const creditCardFee = round2(totalBeforeCCFee * CREDIT_CARD_FEE_RATE);
  return round2(totalBeforeCCFee + creditCardFee);
};
/**
 * QuotePreview Component
 * Sidebar drawer preview of complete quote before sending
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether drawer is open
 * @param {Function} props.onClose - Callback to close drawer
 * @param {Object} props.quoteData - Complete quote data
 * @param {Function} props.onSend - Callback to send quote
 * @param {Function} props.onSaveDraft - Callback to save quote as draft
 * @param {boolean} props.isSending - Whether quote is being sent
 * @param {boolean} props.isSavingDraft - Whether draft is being saved
 */
const QuotePreview = ({
  isOpen = false,
  onClose,
  quoteData,
  onSend,
  onSaveDraft,
  isSending = false,
  isSavingDraft = false,
}) => {

  // Get selectedTripGroup and shortlistAircraft from store
  const { selectedTripGroup, shortlistAircraft } = useAppStore();
  console.log("quoteData", quoteData);

  // Safe data access
  const client = quoteData?.client?.clientData || {};
  const flights = quoteData?.flights || [];
  const aircraft = quoteData?.aircraft?.aircraft;
  const settings = quoteData?.settings || {};
  const flightType = quoteData?.flightType || 'one-way';
  const operator = quoteData?.aircraft?.operator || {};
  const bidOptions = quoteData?.bidOptions || [];

  // Get passenger count and leg multiplier for price calculation
  const passengerCount = flights?.[0]?.passengerCount || 1;
  const legMultiplier = flightType === 'round-trip' ? 2 : (flightType === 'multi-leg' ? flights.length : 1);

  // Get display price for an option (uses calculated value if final_price not set)
  const getOptionPrice = (option) => calculateFinalPrice(option, passengerCount, legMultiplier);

  // Handle send
  const handleSend = () => {
    if (onSend && !isSending) {
      // Sum up all final_price from shortlisted bid options (use calculated if not set)
      const totalPrice = bidOptions?.reduce((sum, option) => sum + getOptionPrice(option), 0) || 0;
      const totalCost = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.total_price) || 0), 0) || 0;
      const fet = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.federal_excise_tax) || 0), 0) || 0;
      const segtax = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_tax) || 0), 0) || 0;
      const passengers = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_pax_count) || 0), 0) || 0;
      
      quoteData.settings.platform_fee = round2(totalCost * 0.015);
      quoteData.settings.broker_margin = round2(totalCost * 0.15);
      quoteData.settings.final_price = totalPrice;
      quoteData.settings.federal_excise_tax = fet;
      quoteData.settings.segment_tax = segtax;
      quoteData.settings.total_passengers = passengers;
      onSend(quoteData);
    }
  };

  // Handle save draft
  const handleSaveDraft = () => {
    if (onSaveDraft && !isSending) {
      const totalPrice = bidOptions?.reduce((sum, option) => sum + getOptionPrice(option), 0) || 0;
      const totalCost = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.total_price) || 0), 0) || 0;
      const fet = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.federal_excise_tax) || 0), 0) || 0;
      const segtax = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_tax) || 0), 0) || 0;
      const passengers = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_pax_count) || 0), 0) || 0;
      
      quoteData.settings.final_price = totalPrice;
      quoteData.settings.platform_fee = round2(totalCost * 0.015);
      quoteData.settings.broker_margin = round2(totalCost * 0.15);
      quoteData.settings.federal_excise_tax = fet;
      quoteData.settings.segment_tax = segtax;
      quoteData.settings.total_passengers = passengers;
      onSaveDraft(quoteData);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent side="right" className="w-full max-w-md lg:max-w-2xl flex flex-col" closeOnOutsideClick={false}>
        <DrawerHeader className="sr-only">
          <DrawerTitle>Quote Preview</DrawerTitle>
        </DrawerHeader>
        {/* Header */}
        <div className="flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex-1">
            <h2 className="font-semibold text-gray-900 text-2xl">
              {settings.bidTitle || 'Miami Trip (TEB - MIA)'}
            </h2>
            <p className="text-lg text-gray-500 mt-0.5">
              Quote for {client.first_name || client.firstName} {client.last_name || client.lastName}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-24">

          {/* Trip Details Section */}
          <div className="px-4 pt-4 pb-6">
            <div className="border boder-gray-50 rounded-xl overflow-hidden">
              <div className="p-4">
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">TRIP DETAILS</h3>
                {/* Two Column Layout - Passenger Count and Round Trip */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Passenger Count */}
                  <div className="flex items-start gap-2.5">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                      <RiTeamLine className="h-4 w-4 text-gray-600"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1">Passenger Count</p>
                      <p className="text-base font-semibold text-gray-900">
                        {flights[0]?.passengerCount || 6}
                      </p>
                    </div>
                  </div>

                  {/* Round Trip */}
                  <div className="flex items-start gap-2.5">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                      <RiMapPinLine className="h-4 w-4 text-gray-600"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1">
                        {flightType === 'round-trip' ? 'Round Trip' : flightType === 'multi-leg' ? 'Multi-Leg' : 'One-Way'}
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        {
                          flights.map((leg, i) => (
                              <span key={i}>
                                {leg.departure?.icao} → {leg.arrival?.icao}
                                {i < flights.length - 1 && <br />}
                              </span>
                            ))
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-4">
                  {
                    flights.map((flight, idx) => (
                      <div key={idx} className="flex items-start gap-2.5">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                          <RiCalendar2Line className="h-4 w-4 text-gray-600"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-600 mb-1">
                            {flight.departure?.icao} → {flight.arrival?.icao}
                          </p>
                          <p className="text-base font-semibold text-gray-900">
                            {formatDateWithTime(flight.departureDate, flight.departureTime)}
                          </p>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>

              {/* Expiration Bar */}
              <div className="bg-gray-100 px-4 py-1 flex items-center justify-center gap-1.5 mx-4 mb-4 rounded-2xl">
                <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-xs text-gray-600">
                  {settings.validUntil ? `Expires on ${formatDate(settings.validUntil)}` : 'No expiration date'}
                </p>
              </div>
            </div>
          </div>

          {/* Aircraft Section */}
          {bidOptions && bidOptions.length > 0 && (
            <div className="px-4 pb-6">
              {bidOptions
                .filter(option => option.shortlisted === true)
                .map((option, index) => {
                  const optionAircraft = option.aircraft;
                  const optionOperator = option.aircraft.operator || {};
                  const features = option.aircraft.aircraft_features || {};
                  return (
                  <div key={option.bid_option_id || index} className="border border-gray-200 rounded-2xl mb-4">
                    {/* Aircraft Image */}
                    <div className="rounded-t-2xl overflow-hidden">
                      <ImageCarousel
                        images={(() => {
                          const photos = getAircraftPhotos(optionAircraft, true);
                          return Array.isArray(photos) ? photos : [photos];
                        })()}
                        alt={getAircraftModelName(optionAircraft)}
                        className="h-[350px]"
                      />
                    </div>

                    {/* Aircraft Details */}
                    <div className="px-4 pb-3 pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold text-gray-900">{getAircraftModelName(optionAircraft)}</h3>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <RiPlaneLine className="h-5 w-5"/>
                            {optionAircraft?.tail_number}
                          </p>
                        </div>
                        {/*
                        <TrustScore score={optionOperator?.trust_score || 0} size="sm" />
                        */}
                      </div>

                      {/* Operator */}
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                        <RiBuildingLine className="h-5 w-5"/>
                        <span>{getOperatorWithLocation(optionOperator)}</span>
                      </div>

                      {/* Price */}
                      <div className="inline-flex items-center px-2 py-1 rounded-2xl gap-2 mb-4 bg-accent">
                        <RiPriceTag3Line className="h-5 w-5 font-bold text-[#0C3E71]" />
                        <span className="text-md font-normal text-[#0C3E71]">{formatPrice(getOptionPrice(option))}</span>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {optionOperator?.argus_rating && optionOperator?.argus_rating!="No" &&
                          <span className={`px-2.5 py-1 bg-gray-200 text-[#171717] text-xs font-medium rounded-2xl flex items-center gap-1`}>
                            <RiTrophyLine className="h-5 w-5 text-gray-600"/> {optionOperator?.argus_rating.toUpperCase()}
                          </span>}
                          
                          {optionOperator?.wyvern_rating && optionOperator?.wyvern_rating!="No" &&
                          <span className={`px-2.5 py-1 bg-gray-200 text-[#171717] text-xs font-medium rounded-2xl flex items-center gap-1`}>
                            <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
                          </span>
                          }

                          {features?.no_aog &&
                          <span className={`px-2.5 py-1 bg-gray-200 text-[#171717] text-xs font-medium rounded-2xl flex items-center gap-1`}>
                            <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
                          </span>
                          }
                      </div>

                      
                      <div className="rounded-xl mt-3">
                        <h6 className="text-sm text-gray-500 font-[500] mb-2">Amenities</h6>

                        {/* OLD Amenities
                        <div className="flex flex-wrap gap-2 mt-2">
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_wifi ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} WiFi 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_entertainment_system ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Entertainment System 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_full_gallery ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Full Gallery 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_enclosed_lavatory ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Enclosed Lavatory 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_satellite_phone ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Satellite Phone 
                        </span>
                        {amenities?.additional_amenities?.length > 0 &&
                          amenities.additional_amenities.map((amenity, idx) => (
                            <span key={idx} className="flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200">
                              <RiCheckLine className="h-4 w-4" /> {amenity}
                            </span>
                          ))
                        } 
                        </div>
                        */}

                        {features?.custom_features && features?.custom_features?.length > 0 ? (
                            <div className="grid grid-cols-1 gap-1">
                              {features?.custom_features.map((feature, index) => (
                                <div key={index} className="flex border-b py-1">
                                  <div className="w-10 h-7 flex items-center justify-center flex-shrink-0">
                                    <RiCheckLine className="w-5 h-5 text-gray-500" />
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-500">{feature}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                        ): (
                          <p className="text-sm text-gray-400">No amenities included.</p>
                        )}

    
                    </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-3 pb-16 bg-white flex-shrink-0">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSending || isSavingDraft}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-3xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 font-medium"
            >
              {isSavingDraft ? (
                <>
                  <RiLoader4Line className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <RiDraftLine className="h-4 w-4"/>
                  Save Draft
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending || isSavingDraft}
              className="flex-1 px-3 py-2 text-sm bg-primary-500 text-white rounded-3xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 font-medium"
            >
              {isSending ? (
                <>
                  <RiLoader4Line className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RiSendPlane2Line className="h-4 w-4"/>
                  Send Quote to Client
                </>
              )}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default QuotePreview;
